import { OwnAccountDTO } from '../dtos/own-account.dto';
import { UserDetailsDto } from '../dtos/user-details.dto';

/**
 * Restricts a candidate user list to only those who are grantees on at least one account.
 * Co-owners (SHJS/SHMN/SHPS/TRTR) are never actual grantees, but can share a clientPoid
 * with a real grantee on a different account, so filtering must check grantees[] across
 * ALL accounts rather than just the target account.
 *
 * Shared between src/app and src/app-multi-account, which both re-export it from their
 * local account-permission.util.ts, so the logic can't diverge between the two trees.
 * @param users - Candidate users to filter
 * @param accounts - All of the grantor's accounts, used to build the set of known grantee POIDs
 * @returns Users who are present in at least one account's grantees[] entry. If `accounts` is
 * undefined or empty, there is no known grantee data to check against, so this returns an empty
 * array regardless of `users` — callers with no account data available should skip calling this
 * function rather than relying on it to pass users through unfiltered.
 */
export const filterUsersToExistingGranteesAcrossAccounts = (
    users: UserDetailsDto[] | undefined,
    accounts: OwnAccountDTO[] | undefined,
): UserDetailsDto[] => {
    if (!users?.length || !accounts?.length) {
        return [];
    }

    const granteePoids = new Set(
        accounts.flatMap((account) => (account.grantees ?? []).map((grantee) => grantee.clientPoid)),
    );

    return users.filter((user) => granteePoids.has(user.clientPoid));
};

export default {
    filterUsersToExistingGranteesAcrossAccounts,
};
